import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";
import { Box, Stack, Typography } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  IOTHeartbeatNotificationMutateModal,
  IOTHeartbeatNotificationRow,
} from "@dmca/frontend/widgets/admin";
import { IIOTDeviceNotification } from "@dmca/models/interface/iot-device";
import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined";
import { Button } from "@dmca/frontend/components/button";

export const Route = createFileRoute(
  "/_authed/_drawerLayout/admin/notification/iot-heartbeat"
)({
  component: RouteComponent,
  loader: async ({ context: { trpcQcProxy: trpc, qc } }) => {
    qc.prefetchQuery(trpc.plant.getAll.queryOptions());
    const [devices, notifications, users] = await Promise.all([
      qc.ensureQueryData(trpc.iot.getAllWithStatus.queryOptions()),
      qc.ensureQueryData(trpc.iot.getAllNotificationContacts.queryOptions()),
      qc.ensureQueryData(trpc.user.getAll.queryOptions()),
    ]);
    return {
      devices,
      notifications,
      users,
    };
  },
});

type DeviceIdSelection = { id: string; type: "plant" | "device" | "all" };

function RouteComponent() {
  const { trpcQcProxy: trpc, qc } = Route.useRouteContext();
  const {
    devices: initialDevices,
    notifications: initialNotifications,
    users: initialUsers,
  } = Route.useLoaderData();
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<
    DeviceIdSelection[]
  >([]);
  const [deviceIdsForCreation, setDeviceIdsForCreation] = useState<
    DeviceIdSelection[]
  >([]);
  const [isMultiSelect, setIsMultiSelect] = useState(false);

  const [selectedNotificationsForEdit, setSelectedNotificationsForEdit] =
    useState<IIOTDeviceNotification[]>([]);

  const [mutationMode, setMutationMode] = useState<"closed" | "add" | "update">(
    "closed"
  );

  const { data: devices } = useQuery(
    trpc.iot.getAllWithStatus.queryOptions(undefined, {
      select: (data) =>
        data.sort((a, b) => a.defaultName.localeCompare(b.defaultName)),
      initialData: initialDevices,
    })
  );

  const { data: plants } = useQuery(
    trpc.plant.getAll.queryOptions(undefined, {
      select: (data) => data.sort((a, b) => a.name.localeCompare(b.name)),
    })
  );

  const { data: users } = useQuery(
    trpc.user.getAll.queryOptions(undefined, {
      select: (data) => data.sort((a, b) => a.name.localeCompare(b.name)),
      initialData: initialUsers,
    })
  );

  const { data: notifications } = useQuery(
    trpc.iot.getAllNotificationContacts.queryOptions(undefined, {
      initialData: initialNotifications,
    })
  );

  const deleteMutation = useMutation(
    trpc.iot.deleteNotificationContact.mutationOptions({
      onMutate: (vars) => {
        qc.setQueryData(
          trpc.iot.getAllNotificationContacts.queryKey(),
          (oldData) =>
            oldData?.filter((x) => !vars.some((n) => n.id === x.id)) || []
        );
      },
      onSettled: () => {
        qc.invalidateQueries(trpc.iot.getAllNotificationContacts.queryFilter());
      },
    })
  );

  return (
    <>
      <Stack
        sx={{
          flexDirection: "column",
          width: "max-content",
        }}
      >
        <Stack direction="row" spacing={2}>
          <Button
            startIcon={isMultiSelect ? undefined : <CheckBoxOutlinedIcon />}
            size="small"
            variant="text"
            color={isMultiSelect ? "error" : "primary"}
            onClick={() => {
              setDeviceIdsForCreation([]);
              setIsMultiSelect((prev) => !prev);
            }}
            sx={{
              p: 1,
              m: 0,
            }}
          >
            {isMultiSelect ? "Cancel" : "Select Multiple"}
          </Button>
          {!isMultiSelect && (
            <Button
              startIcon={
                selectedDeviceIds.length ===
                (devices ?? []).length + (plants ?? []).length + 1 ? ( // all devices is one
                  <ExpandLess />
                ) : (
                  <ExpandMore />
                )
              }
              size="small"
              variant="text"
              sx={{ p: 1, m: 0 }}
              onClick={() => {
                setSelectedDeviceIds((prev) =>
                  prev.length ===
                  (devices ?? []).length + (plants ?? []).length + 1 // all devices is one
                    ? []
                    : [
                        ...(devices ?? []).map((device) => ({
                          id: device.id,
                          type: "device" as const,
                        })),
                        ...(plants ?? []).map((plant) => ({
                          id: plant.id,
                          type: "plant" as const,
                        })),
                        {
                          type: "all" as const,
                          id: "",
                        },
                      ]
                );
              }}
            >
              {selectedDeviceIds.length !==
              (devices ?? []).length + (plants ?? []).length + 1 // all devices is one
                ? "Expand"
                : "Collapse"}{" "}
              All
            </Button>
          )}
          {isMultiSelect && deviceIdsForCreation.length > 0 && (
            <Button
              size="small"
              variant="text"
              sx={{ p: 1, m: 0 }}
              onClick={() => {
                setMutationMode("add");
              }}
            >
              Add Notifications
            </Button>
          )}
        </Stack>
        <IOTHeartbeatNotificationRow
          label="All Devices"
          showCheckbox={isMultiSelect}
          checkboxChecked={deviceIdsForCreation.some(
            (item) => item.type === "all"
          )}
          onCheckboxChange={(checked) => {
            setDeviceIdsForCreation((prev) => {
              if (checked) {
                return [...prev, { type: "all" as const, id: "" }];
              } else {
                return prev.filter((item) => item.type !== "all");
              }
            });
          }}
          onAddButtonClick={() => {
            setDeviceIdsForCreation([{ id: "", type: "all" }]);
            setMutationMode("add");
          }}
          onEditButtonClick={(notifications) => {
            setSelectedNotificationsForEdit(notifications);
            setMutationMode("update");
          }}
          onDeleteButtonClick={(notifications) => {
            deleteMutation.mutate(
              notifications.map((x) => ({
                id: x.id,
              }))
            );
          }}
          notifications={(notifications ?? []).filter((x) => x.allDevices)}
          expanded={
            !isMultiSelect &&
            selectedDeviceIds.some((item) => item.type === "all")
          }
          users={users ?? []}
          onCollapseButtonClick={() => {
            setSelectedDeviceIds((prev) =>
              prev.some((item) => item.type === "all")
                ? prev.filter((item) => item.type !== "all")
                : [
                    ...prev,
                    {
                      id: "",
                      type: "all" as const,
                    },
                  ]
            );
          }}
        />
        {plants?.map((plant) => (
          <Box key={plant.id} data-testid="iot-plant-row">
            <Typography fontWeight={600}>{plant.name}</Typography>
            <IOTHeartbeatNotificationRow
              label={plant.name}
              showCheckbox={isMultiSelect}
              checkboxChecked={deviceIdsForCreation.some(
                (item) => item.type === "plant" && item.id === plant.id
              )}
              onCheckboxChange={(checked) => {
                setDeviceIdsForCreation((prev) => {
                  if (checked) {
                    return [...prev, { type: "plant" as const, id: plant.id }];
                  } else {
                    return prev.filter(
                      (item) => item.type !== "plant" || item.id !== plant.id
                    );
                  }
                });
              }}
              onAddButtonClick={() => {
                setDeviceIdsForCreation([{ id: plant.id, type: "plant" }]);
                setMutationMode("add");
              }}
              onEditButtonClick={(notifications) => {
                setSelectedNotificationsForEdit(notifications);
                setMutationMode("update");
              }}
              onDeleteButtonClick={(notifications) => {
                deleteMutation.mutate(
                  notifications.map((x) => ({
                    id: x.id,
                  }))
                );
              }}
              notifications={(notifications ?? []).filter(
                (x) => x.plantId === plant.id
              )}
              expanded={
                !isMultiSelect &&
                selectedDeviceIds.some(
                  (item) => item.type === "plant" && item.id === plant.id
                )
              }
              users={users ?? []}
              onCollapseButtonClick={() => {
                setSelectedDeviceIds((prev) =>
                  prev.some(
                    (item) => item.type === "plant" && item.id === plant.id
                  )
                    ? prev.filter(
                        (item) => item.type !== "plant" || item.id !== plant.id
                      )
                    : [
                        ...prev,
                        {
                          id: plant.id,
                          type: "plant" as const,
                        },
                      ]
                );
              }}
            />
            {devices
              ?.filter((device) => device.cellLocation.plantId === plant.id)
              .sort((x, y) => x.defaultName.localeCompare(y.defaultName))
              .map((device) => (
                <IOTHeartbeatNotificationRow
                  key={device.id}
                  data-testid="iot-device-row"
                  showCheckbox={isMultiSelect}
                  onCheckboxChange={(checked) => {
                    setDeviceIdsForCreation((prev) => {
                      if (checked) {
                        return [...prev, { id: device.id, type: "device" }];
                      } else {
                        return prev.filter(
                          (item) =>
                            item.type !== "device" || item.id !== device.id
                        );
                      }
                    });
                  }}
                  checkboxChecked={deviceIdsForCreation.some(
                    (item) => item.type === "device" && item.id === device.id
                  )}
                  expanded={
                    !isMultiSelect &&
                    selectedDeviceIds.some(
                      (item) => item.type === "device" && item.id === device.id
                    )
                  }
                  onCollapseButtonClick={() => {
                    setSelectedDeviceIds((prev) =>
                      prev.some(
                        (item) =>
                          item.type === "device" && item.id === device.id
                      )
                        ? prev.filter(
                            (item) =>
                              item.type !== "device" || item.id !== device.id
                          )
                        : [
                            ...prev,
                            {
                              id: device.id,
                              type: "device" as const,
                            },
                          ]
                    );
                  }}
                  label={device.defaultName}
                  onAddButtonClick={() => {
                    setDeviceIdsForCreation([
                      { id: device.id, type: "device" },
                    ]);
                    setMutationMode("add");
                  }}
                  onEditButtonClick={(notifications) => {
                    setSelectedNotificationsForEdit(notifications);
                    setMutationMode("update");
                  }}
                  onDeleteButtonClick={(notifications) => {
                    deleteMutation.mutate(
                      notifications.map((x) => ({
                        id: x.id,
                      }))
                    );
                  }}
                  notifications={(notifications ?? []).filter(
                    (x) => x.deviceId === device.id
                  )}
                  users={users ?? []}
                />
              ))}
          </Box>
        ))}
      </Stack>
      {mutationMode !== "closed" && (
        <IOTHeartbeatNotificationMutateModal
          open={true}
          type={mutationMode}
          header={`${
            mutationMode === "add" ? "Create" : "Update"
          } Notifications for ${
            mutationMode === "add"
              ? !isMultiSelect
                ? deviceIdsForCreation[0]?.type === "device"
                  ? devices?.find((x) => x.id === deviceIdsForCreation[0].id)
                      ?.defaultName
                  : deviceIdsForCreation[0]?.type === "all"
                  ? "All Devices"
                  : plants?.find((x) => x.id === deviceIdsForCreation[0]?.id)
                      ?.name
                : "Multiple Devices"
              : selectedNotificationsForEdit[0]?.deviceId
              ? devices?.find(
                  (x) => x.id === selectedNotificationsForEdit[0].deviceId
                )?.defaultName
              : selectedNotificationsForEdit[0]?.plantId
              ? plants?.find(
                  (x) => x.id === selectedNotificationsForEdit[0].plantId
                )?.name
              : "All Devices"
          }`}
          onClose={() => {
            setMutationMode("closed");
            setSelectedNotificationsForEdit([]);
            setIsMultiSelect(false);
            setDeviceIdsForCreation([]);
          }}
          users={users || []}
          devices={
            deviceIdsForCreation.length > 0
              ? deviceIdsForCreation
              : [
                  selectedNotificationsForEdit[0]?.deviceId
                    ? {
                        type: "device" as const,
                        id: selectedNotificationsForEdit[0].deviceId,
                      }
                    : selectedNotificationsForEdit[0]?.plantId
                    ? {
                        type: "plant" as const,
                        id: selectedNotificationsForEdit[0].plantId,
                      }
                    : {
                        type: "all" as const,
                      },
                ]
          }
          notifications={selectedNotificationsForEdit}
        />
      )}
    </>
  );
}
